import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Mail, CheckCircle, Globe, AlertTriangle, Clock, User, Building, Briefcase, Download } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { ProfileEmailDialog } from "./ProfileEmailDialog";
import { ProfileCommunicationHistory } from "./ProfileCommunicationHistory";
import { 
  REQUIRED_FIELDS, 
  getProfileCompletionStatus, 
  getRelativeTime,
  getLastActivityDate,
  type ProfileCompletionData 
} from "@/lib/profileCompletion";
import { useGrantDualRole } from "@/hooks/useDualRoleRequestsAdmin";

interface ProfileReviewDialogProps {
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActionComplete: () => void;
}

// Role-specific required fields — unified with REQUIRED_FIELDS from profileCompletion.ts
// Base fields (all roles): full_name, email, country, phone, linkedin_url, gender, age_range, referred_by
// Mentor additions: professional_background, years_experience
const ENTREPRENEUR_ROLE_FIELDS = {
  profile: ["full_name", "gender", "age_range", "referred_by"],
  private: ["email", "country", "phone", "linkedin_url"],
};

const MENTOR_ROLE_FIELDS = {
  profile: ["full_name", "gender", "age_range", "referred_by"],
  mentor: ["professional_background", "years_experience"],
  private: ["email", "country", "phone", "linkedin_url"],
};

const FIELD_LABELS: Record<string, string> = {
  full_name: "Full Name",
  bio: "Bio / About",
  avatar_url: "Profile Photo",
  company_name: "Company Name",
  company_description: "Company Description",
  startup_stage: "Business Stage",
  target_market: "Target Market",
  funding_status: "Funding Status",
  specific_challenges: "Specific Challenges",
  professional_background: "Professional Background",
  years_experience: "Years of Experience",
  career_highlights: "Career Highlights",
  availability_notes: "Availability Notes",
  email: "Email Address",
  phone: "Phone Number",
  linkedin_url: "LinkedIn URL",
  parish: "Parish",
  country: "Country",
  referred_by: "Referred By",
  gender: "Gender",
  age_range: "Age Range",
  education_level: "Education Level",
  has_device: "Has Device",
};

const NOT_PROVIDED = "— Not provided —";

// List of required fields for highlighting in the modal
const REQUIRED_FIELD_KEYS = REQUIRED_FIELDS.map(f => f.key);

export function ProfileReviewDialog({
  profileId,
  open,
  onOpenChange,
  onActionComplete,
}: ProfileReviewDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [downloadingDeck, setDownloadingDeck] = useState(false);
  const [confirmRevokeRole, setConfirmRevokeRole] = useState<'mentor' | 'entrepreneur' | null>(null);
  const grantDualRole = useGrantDualRole();

  const revokeDualRole = useMutation({
    mutationFn: async (roleToRevoke: 'mentor' | 'entrepreneur') => {
      const { error } = await supabase.rpc('revoke_dual_role', {
        p_user_id: profileId,
        p_role_to_revoke: roleToRevoke,
      });
      if (error) {
        console.error('Full Supabase error:', JSON.stringify(error, null, 2));
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile_review_full', profileId] });
      queryClient.invalidateQueries({ queryKey: ['pending_profiles_review'] });
      queryClient.invalidateQueries({ queryKey: ['dual_role_requests_admin'] });
      toast.success('Role capability revoked');
    },
    onError: (error: Error) => {
      toast.error('Failed to revoke role: ' + error.message);
    },
  });

  // Fetch pitch deck for entrepreneurs
  const { data: pitchDeck } = useQuery({
    queryKey: ["admin_pitch_deck", profileId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pitch_decks")
        .select("id, file_key")
        .eq("user_id", profileId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open && !!profileId,
  });

  const handleDownloadPitchDeck = async () => {
    if (!pitchDeck?.file_key) return;
    setDownloadingDeck(true);
    try {
      const { data, error } = await supabase.storage
        .from("pitch-decks")
        .createSignedUrl(pitchDeck.file_key, 60);
      if (error || !data?.signedUrl) throw error || new Error("No URL returned");
      window.open(data.signedUrl, "_blank");
    } catch (err: any) {
      toast.error("Unable to download pitch deck. Please try again.");
    } finally {
      setDownloadingDeck(false);
    }
  };

  // Fetch complete profile data
  const { data: profileData, isLoading } = useQuery({
    queryKey: ["profile_review_full", profileId],
    queryFn: async () => {
      // Fetch main profile
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", profileId)
        .single();

      if (profileError) throw profileError;

      // Fetch private data
      const { data: privateData } = await supabase
        .from("profile_private")
        .select("*")
        .eq("user_id", profileId)
        .maybeSingle();

      // Fetch role-specific data
      let roleData = null;
      if (profile.role === "entrepreneur") {
        const { data } = await supabase
          .from("entrepreneur_profiles")
          .select("*")
          .eq("id", profileId)
          .maybeSingle();
        roleData = data;
      } else if (profile.role === "mentor") {
        const { data } = await supabase
          .from("mentor_profiles")
          .select("*")
          .eq("id", profileId)
          .maybeSingle();
        roleData = data;
      }

      // Fetch skills and industries
      const { data: skills } = await supabase
        .from("user_skills")
        .select("skill_id, skills(id, name)")
        .eq("user_id", profileId);

      const { data: industries } = await supabase
        .from("user_industries")
        .select("industry_id, industries(id, name)")
        .eq("user_id", profileId);

      return {
        profile,
        privateData,
        roleData,
        skills: skills?.map((s: any) => s.skills) || [],
        industries: industries?.map((i: any) => i.industries) || [],
      };
    },
    enabled: open && !!profileId,
  });

  // Calculate completion status using the shared utility
  const completionResult = useMemo(() => {
    if (!profileData) return null;
    
    const { profile, privateData } = profileData;
    const completionData: ProfileCompletionData = {
      profile: {
        full_name: profile.full_name,
        bio: profile.bio,
        gender: profile.gender,
        age_range: profile.age_range,
        referred_by: profile.referred_by,
        is_published: profile.is_published,
        updated_at: profile.updated_at,
        created_at: profile.created_at,
      },
      privateData: privateData ? {
        email: privateData.email,
        phone: privateData.phone,
        linkedin_url: privateData.linkedin_url,
        country: privateData.country,
        updated_at: privateData.updated_at,
      } : null,
    };
    
    return getProfileCompletionStatus(completionData);
  }, [profileData]);

  // Calculate role-specific missing fields (for the role requirements checklist)
  const getMissingFields = () => {
    if (!profileData) return { missing: [], present: [] };

    const { profile, privateData, roleData } = profileData;
    const requiredFields =
      profile.role === "entrepreneur" ? ENTREPRENEUR_ROLE_FIELDS : MENTOR_ROLE_FIELDS;

    const missing: string[] = [];
    const present: string[] = [];

    // Check profile fields
    requiredFields.profile.forEach((field) => {
      if (!profile[field] || profile[field]?.trim() === "") {
        missing.push(field);
      } else {
        present.push(field);
      }
    });

    // Check role-specific fields
    const roleKey = profile.role === "entrepreneur" ? "entrepreneur" : "mentor";
    (requiredFields as any)[roleKey]?.forEach((field: string) => {
      if (!roleData || !roleData[field] || roleData[field]?.toString().trim() === "") {
        missing.push(field);
      } else {
        present.push(field);
      }
    });

    // Check private fields
    requiredFields.private.forEach((field) => {
      if (!privateData || !privateData[field] || privateData[field]?.trim() === "") {
        missing.push(field);
      } else {
        present.push(field);
      }
    });

    return { missing, present };
  };

  const { missing: missingFields, present: presentFields } = getMissingFields();
  
  // Get last activity info
  const lastActivityInfo = useMemo(() => {
    if (!profileData) return null;
    const completionData: ProfileCompletionData = {
      profile: {
        updated_at: profileData.profile.updated_at,
        created_at: profileData.profile.created_at,
      },
      privateData: profileData.privateData ? {
        updated_at: profileData.privateData.updated_at,
      } : null,
    };
    const lastActivity = getLastActivityDate(completionData);
    return {
      date: lastActivity,
      relative: getRelativeTime(lastActivity),
    };
  }, [profileData]);

  // Verify profile mutation
  const verifyProfile = useMutation({
    mutationFn: async () => {
      // First check if welcome email was already sent
      const { data: existingEmail } = await supabase
        .from("profile_admin_emails")
        .select("id")
        .eq("profile_id", profileId)
        .eq("template_type", "welcome_approved")
        .in("status", ["sent", "queued"])
        .maybeSingle();

      // Update profile to verified
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          is_verified: true,
          admin_verified_at: new Date().toISOString(),
          admin_verified_by: user?.id,
        })
        .eq("id", profileId);

      if (updateError) throw updateError;

      // If no welcome email was sent, send it now
      if (!existingEmail) {
        const { error: emailError } = await supabase.functions.invoke("admin-send-welcome-approved", {
          body: { profileId },
        });

        if (emailError) {
          console.error("Welcome email failed:", emailError);
          return { emailSent: false };
        }
        return { emailSent: true };
      }

      return { emailSent: false, alreadySent: true };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["pending_profiles_review"] });
      queryClient.invalidateQueries({ queryKey: ["profile_review_full", profileId] });
      
      if (result.alreadySent) {
        toast.success("Profile verified — welcome email was already sent.");
      } else if (result.emailSent) {
        toast.success("Profile verified — welcome email sent successfully!");
      } else {
        toast.warning("Profile verified — but welcome email failed to send. You may retry manually.");
      }
      
      onActionComplete();
    },
    onError: (error) => {
      toast.error("Failed to verify profile: " + error.message);
    },
  });

  // Publish profile mutation
  const publishProfile = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("profiles")
        .update({ is_published: true })
        .eq("id", profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending_profiles_review"] });
      queryClient.invalidateQueries({ queryKey: ["profile_review_full", profileId] });
      toast.success("Profile published successfully!");
      onActionComplete();
    },
    onError: (error) => {
      toast.error("Failed to publish profile: " + error.message);
    },
  });

  // Render a field value with fallback
  const renderField = (value: any, isDate = false) => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-muted-foreground italic">{NOT_PROVIDED}</span>;
    }
    if (isDate && value) {
      return format(new Date(value), "MMM d, yyyy 'at' h:mm a");
    }
    return value;
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!profileData) return null;

  const { profile, privateData, roleData, skills, industries } = profileData;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
          <DialogHeader className="shrink-0 px-6 pt-6">
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Review: {profile.full_name}
            </DialogTitle>
            <DialogDescription>
              Review all profile information before approving or requesting more info
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2 items-center shrink-0 px-6">
            <Badge variant="outline" className="capitalize">
              {profile.role}
            </Badge>
            {profile.is_verified ? (
              <Badge variant="default" className="bg-success">
                <CheckCircle className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            ) : (
              <Badge variant="destructive">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Unverified
              </Badge>
            )}
            {profile.is_published ? (
              <Badge variant="default">
                <Globe className="h-3 w-3 mr-1" />
                Published
              </Badge>
            ) : (
              <Badge variant="secondary">Unpublished</Badge>
            )}
            {completionResult && (
              <Badge className={completionResult.isComplete 
                ? "bg-success text-success-foreground" 
                : "bg-warning text-warning-foreground"
              }>
                {completionResult.isComplete ? (
                  <CheckCircle className="h-3 w-3 mr-1" />
                ) : (
                  <AlertTriangle className="h-3 w-3 mr-1" />
                )}
                {completionResult.statusLabel}
              </Badge>
            )}
            {lastActivityInfo && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                <Clock className="h-3 w-3" />
                Last activity: {lastActivityInfo.relative}
              </div>
            )}
          </div>

          {/* Missing Fields Summary Banner */}
          {completionResult && !completionResult.isComplete && (
            <div className="shrink-0 px-6 overflow-hidden">
            <Alert variant="destructive" className="border-warning bg-warning/10 w-full overflow-hidden">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning break-words whitespace-normal">
                Missing {completionResult.missingFields.length} required field{completionResult.missingFields.length > 1 ? 's' : ''}
              </AlertTitle>
              <AlertDescription className="text-sm break-words whitespace-normal">
                {completionResult.missingFields.map(f => f.label).join(', ')}
              </AlertDescription>
              <div className="mt-2 flex items-center gap-2">
                <Progress 
                  value={(completionResult.completedCount / completionResult.totalCount) * 100} 
                  className="h-2 flex-1"
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {completionResult.completedCount}/{completionResult.totalCount} complete
                </span>
              </div>
            </Alert>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-4 shrink-0 mx-6">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="checklist">
                Checklist
                {missingFields.length > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs">
                    {missingFields.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="private">Private Info</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
                {/* Core Profile */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Core Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div className={!profile.full_name ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Full Name
                          {!profile.full_name && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium">{renderField(profile.full_name)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Role</p>
                        <p className="font-medium capitalize">{profile.role}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Created</p>
                        <p className="font-medium">{renderField(profile.created_at, true)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Profile Photo</p>
                        <p className="font-medium">
                          {profile.avatar_url ? (
                            <a href={profile.avatar_url} target="_blank" rel="noopener" className="text-primary underline">
                              View Photo
                            </a>
                          ) : (
                            renderField(null)
                          )}
                        </p>
                      </div>
                      {/* Role Capabilities — always shown in col-span-2 */}
                      <div className="col-span-2 border rounded-md p-3 space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role Capabilities</p>

                        {/* Mentor capability row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Mentor</span>
                            {profile.is_mentor
                              ? <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                              : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inactive</span>
                            }
                            {profile.role === 'mentor' && (
                              <span className="text-xs text-muted-foreground">(primary role)</span>
                            )}
                          </div>
                          <div>
                            {profile.role === 'mentor' ? (
                              <span className="text-xs text-muted-foreground italic">Cannot revoke primary role</span>
                            ) : profile.is_mentor ? (
                              confirmRevokeRole === 'mentor' ? (
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole(null)}>Cancel</Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    disabled={revokeDualRole.isPending}
                                    onClick={() => revokeDualRole.mutate('mentor', { onSuccess: () => setConfirmRevokeRole(null) })}
                                  >
                                    {revokeDualRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Revoke'}
                                  </Button>
                                </div>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole('mentor')}>
                                  Revoke Mentor
                                </Button>
                              )
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={grantDualRole.isPending}
                                onClick={() => grantDualRole.mutate({ userId: profile.id, roleToGrant: 'mentor' })}
                              >
                                Grant Mentor
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Entrepreneur capability row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Entrepreneur</span>
                            {profile.is_entrepreneur
                              ? <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                              : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inactive</span>
                            }
                            {profile.role === 'entrepreneur' && (
                              <span className="text-xs text-muted-foreground">(primary role)</span>
                            )}
                          </div>
                          <div>
                            {profile.role === 'entrepreneur' ? (
                              <span className="text-xs text-muted-foreground italic">Cannot revoke primary role</span>
                            ) : profile.is_entrepreneur ? (
                              confirmRevokeRole === 'entrepreneur' ? (
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole(null)}>Cancel</Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    disabled={revokeDualRole.isPending}
                                    onClick={() => revokeDualRole.mutate('entrepreneur', { onSuccess: () => setConfirmRevokeRole(null) })}
                                  >
                                    {revokeDualRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Revoke'}
                                  </Button>
                                </div>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => setConfirmRevokeRole('entrepreneur')}>
                                  Revoke Entrepreneur
                                </Button>
                              )
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={grantDualRole.isPending}
                                onClick={() => grantDualRole.mutate({ userId: profile.id, roleToGrant: 'entrepreneur' })}
                              >
                                Grant Entrepreneur
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                    </div>
                    <div>
                      <p className="text-muted-foreground">Bio / About</p>
                      <p className="font-medium whitespace-pre-wrap">{renderField(profile.bio)}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Role-Specific Profile */}
                {profile.role === "entrepreneur" && (
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Entrepreneur Profile
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-muted-foreground">Company Name</p>
                          <p className="font-medium">{renderField(roleData?.company_name)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Business Stage</p>
                          <p className="font-medium capitalize">
                            {renderField(roleData?.startup_stage?.replace(/_/g, " "))}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Target Market</p>
                          <p className="font-medium">{renderField(roleData?.target_market)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Funding Status</p>
                          <p className="font-medium">{renderField(roleData?.funding_status)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Company Description</p>
                        <p className="font-medium whitespace-pre-wrap">
                          {renderField(roleData?.company_description)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Specific Challenges</p>
                        <p className="font-medium whitespace-pre-wrap">
                          {renderField(roleData?.specific_challenges)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {profile.role === "mentor" && (
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        Mentor Profile
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-muted-foreground">Years of Experience</p>
                          <p className="font-medium">{renderField(roleData?.years_experience)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Availability Notes</p>
                          <p className="font-medium">{renderField(roleData?.availability_notes)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Professional Background</p>
                        <p className="font-medium whitespace-pre-wrap">
                          {renderField(roleData?.professional_background)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Career Highlights</p>
                        <p className="font-medium whitespace-pre-wrap">
                          {renderField(roleData?.career_highlights)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Skills & Industries */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Skills & Industries</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Skills</p>
                      <div className="flex flex-wrap gap-1">
                        {skills.length > 0 ? (
                          skills.map((s: any) => (
                            <Badge key={s.id} variant="secondary" className="text-xs">
                              {s.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground italic">{NOT_PROVIDED}</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Industries</p>
                      <div className="flex flex-wrap gap-1">
                        {industries.length > 0 ? (
                          industries.map((i: any) => (
                            <Badge key={i.id} variant="outline" className="text-xs">
                              {i.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground italic">{NOT_PROVIDED}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="checklist" className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Required Fields Checklist
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {missingFields.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-destructive">Missing Fields ({missingFields.length})</p>
                        <div className="space-y-1">
                          {missingFields.map((field) => (
                            <div key={field} className="flex items-center gap-2 text-sm">
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                              <span>{FIELD_LABELS[field] || field}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {presentFields.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-success">Completed Fields ({presentFields.length})</p>
                        <div className="space-y-1">
                          {presentFields.map((field) => (
                            <div key={field} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-success" />
                              <span>{FIELD_LABELS[field] || field}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {missingFields.length === 0 && presentFields.length === 0 && (
                      <p className="text-sm text-muted-foreground">No required fields defined</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                <ProfileCommunicationHistory profileId={profileId} />
              </div>
            </TabsContent>

            <TabsContent value="private" className="mt-0 flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Private Contact Information (Admin Only)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div className={!privateData?.email ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Email
                          {!privateData?.email && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium">{renderField(privateData?.email)}</p>
                      </div>
                      <div className={!privateData?.phone ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Phone
                          {!privateData?.phone && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium">{renderField(privateData?.phone)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Phone Type</p>
                        <p className="font-medium capitalize">{renderField(privateData?.phone_type)}</p>
                      </div>
                      <div className={!privateData?.linkedin_url ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          LinkedIn
                          {!privateData?.linkedin_url && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium">
                          {privateData?.linkedin_url ? (
                            <a href={privateData.linkedin_url} target="_blank" rel="noopener" className="text-primary underline">
                              View Profile
                            </a>
                          ) : (
                            renderField(null)
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Parish</p>
                        <p className="font-medium capitalize">{renderField(privateData?.parish?.replace(/_/g, " "))}</p>
                      </div>
                      <div className={!privateData?.country ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Country
                          {!privateData?.country && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium capitalize">{renderField(privateData?.country?.replace(/_/g, " "))}</p>
                      </div>
                    </div>

                    <Separator />

                    <p className="text-xs text-muted-foreground font-medium">Demographics (Internal Research Only)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className={!profile.referred_by ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Referred By
                          {!profile.referred_by && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium capitalize">{renderField(profile.referred_by?.replace(/_/g, " "))}</p>
                      </div>
                      <div className={!profile.gender ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Gender
                          {!profile.gender && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium capitalize">{renderField(profile.gender?.replace(/_/g, " "))}</p>
                      </div>
                      <div className={!profile.age_range ? "p-2 rounded border-2 border-destructive bg-destructive/5" : ""}>
                        <p className="text-muted-foreground flex items-center gap-1">
                          Age Range
                          {!profile.age_range && <AlertTriangle className="h-3 w-3 text-destructive" />}
                        </p>
                        <p className="font-medium">{renderField(profile.age_range)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Education Level</p>
                        <p className="font-medium capitalize">{renderField(profile.education_level?.replace(/_/g, " "))}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Has Device</p>
                        <p className="font-medium capitalize">{renderField(profile.has_device)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          <Separator className="shrink-0" />

          <DialogFooter className="flex-col sm:flex-row gap-2 shrink-0 bg-background px-6 pb-6 pt-3">
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setEmailDialogOpen(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Email Applicant
              </Button>
              {profile.role?.toLowerCase() === "entrepreneur" && (
                pitchDeck ? (
                  <Button variant="outline" onClick={handleDownloadPitchDeck} disabled={downloadingDeck}>
                    {downloadingDeck ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Pitch Deck
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground self-center">No pitch deck generated yet.</span>
                )
              )}
            </div>
            
            <div className="flex gap-2">
              {!profile.is_verified && (
                <Button
                  onClick={() => verifyProfile.mutate()}
                  disabled={verifyProfile.isPending}
                  className="bg-success hover:bg-success/90"
                >
                  {verifyProfile.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Verify Profile
                </Button>
              )}
              
              {!profile.is_published && profile.is_verified && (
                <Button
                  onClick={() => publishProfile.mutate()}
                  disabled={publishProfile.isPending}
                >
                  {publishProfile.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4 mr-2" />
                  )}
                  Publish Profile
                </Button>
              )}

              {!profile.is_published && !profile.is_verified && (
                <Button
                  variant="secondary"
                  onClick={() => publishProfile.mutate()}
                  disabled={publishProfile.isPending}
                >
                  {publishProfile.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4 mr-2" />
                  )}
                  Publish (No Verification)
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <ProfileEmailDialog
        profileId={profileId}
        profileName={profile.full_name}
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        missingFields={missingFields.map((f) => FIELD_LABELS[f] || f)}
      />
    </>
  );
}
